/**
 * CreateSubscriptionModal - Modal premium para criação de Recorrência (Assinatura)
 *
 * REFATORADO (v2.0.0): Clona 100% do layout do CreateChargeModal e reaproveita
 * o ProductCatalogModal com onlyRecurring=true para listar APENAS serviços
 * cuja unidade = 'recorrente'. Frequência, tipo de prazo e ciclos são
 * herdados automaticamente do primeiro serviço selecionado (sem inputs manuais).
 *
 * Travas aplicadas:
 *  - Catálogo filtra unit_type='recorrente' diretamente no RPC (não há UI manual).
 *  - Todos os itens precisam ter a MESMA recurrence_interval. Se o usuário tentar
 *    adicionar um serviço com frequência diferente, é bloqueado com toast.
 *  - Quando o último item é removido, a frequência é destravada.
 *
 * @author Módulo Financeiro - Recorrência
 * @version 2.2.0
 * RELEASE: 2026-09-12 v2.2.0 - Inverte ordem: Conta Corrente antes do Meio de
 *          Pagamento; meios filtrados pela conta (globais sem vínculo aparecem
 *          em todas); troca de conta desmarca meio incompatível.
 * RELEASE: 2026-08-18 v2.1.0 - Veículos da recorrência passam a ser resolvidos
 *          também pelo contato principal do cliente selecionado.
 * RELEASE: 2026-06-09 v2.0.0 - Refatoração total: usa CreateChargeModal como base,
 *          ProductCatalogModal onlyRecurring, frequência travada por item.
 * RELEASE: 2026-06-09 v1.0.0 - Criação inicial (Fase 5 do módulo Recorrência).
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Repeat,
  Plus,
  Trash2,
  Loader2,
  CalendarDays,
  CreditCard,
  Building2,
  Wrench,
  User,
  Check,
  ChevronsUpDown,
  FileText,
  AlertTriangle,
  ShoppingCart,
  Percent,
  Calculator,
  HelpCircle,
} from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { format, addDays } from 'date-fns';

import { ProductCatalogModal } from '@/components/crm/deals/modal/tabs/products/ProductCatalogModal';
import { DealItemsSummary } from '@/components/crm/deals/modal/tabs/products/DealItemsSummary';
import { formatCurrency } from '@/lib/currency-utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCrmProducts, CrmProduct } from '@/hooks/useCrmProducts';
import { useCrmCustomersPaginated, PaginatedCustomer } from '@/hooks/useCrmCustomersPaginated';
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
import { useFinanceiroMeiosPagamento } from '@/hooks/useFinanceiroMeiosPagamento';
import { useFinanceiroContaCorrente } from '@/hooks/useFinanceiroContaCorrente';
import {
  useCreateSubscription,
  FrequenciaAssinatura,
  TipoPrazoAssinatura,
} from '@/hooks/useCreateSubscription';
import {
  SubscriptionJurosSection,
  JurosValue,
  JUROS_DEFAULT,
} from '@/components/financial/subscription/SubscriptionJurosSection';
import { SubscriptionExpiracaoSection } from '@/components/financial/subscription/SubscriptionExpiracaoSection';
import { SavedCardsList } from '@/components/financial/SavedCardsList';
import { VehiclePickerSection } from '@/components/financial/VehiclePickerSection';
import { DescricaoTemplateField } from '@/components/financial/DescricaoTemplateField';
// v1.4.0 (2026-06-20): prefill de juros/multa e expiração via padrões do tenant
import { useChargeDefaults } from '@/hooks/useChargeDefaults';


import {
  RecurrenceInterval,
  TermType,
  TermUnit,
  formatRecurrenceLabel,
  formatTermLabel,
} from '@/lib/crm/recurringServiceUtils';
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('CreateSubscriptionModal');

/** Props do componente */
interface CreateSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Valor mínimo exigido pelo provider Asaas (em BRL) */
const VALOR_MINIMO_COBRANCA = 5;

/** Modo de desconto por item (% ou R$) */
type DiscountMode = 'percent' | 'fixed';

/** Item interno do formulário — espelha o do CreateChargeModal, com metadados de recorrência. */
interface FormItem {
  id: string;                       // ID temporário (key React)
  product_id: string;
  product_name: string;
  product_sku: string | null;
  descricao: string;
  quantidade: number;
  valor_unitario: number;           // Preço por ciclo (recurring_price)
  desconto_percent: number;
  desconto_fixed: number;
  // Metadados travados na adição do item
  recurrence_interval: RecurrenceInterval;
  term_type: TermType | null;
  term_quantity: number | null;
  term_unit: TermUnit | null;
}

/** Conversão de prazo do produto em ciclos totais da assinatura. */
function calcularCiclosTotal(
  termType: TermType | null,
  termQty: number | null,
  termUnit: TermUnit | null,
  recurrence: RecurrenceInterval,
): number | null {
  if (termType !== 'determinado' || !termQty || !termUnit) return null;

  // Converte tudo para meses (aprox.) e divide pela frequência (em meses)
  const termMonths =
    termUnit === 'anos' ? termQty * 12 :
    termUnit === 'meses' ? termQty :
    Math.max(1, Math.round((termQty / 30))); // dias → meses aprox.

  // Meses por ciclo da frequência
  const monthsPerCycle: Record<RecurrenceInterval, number> = {
    semanal: 0.25,
    quinzenal: 0.5,
    mensal: 1,
    bimestral: 2,
    trimestral: 3,
    semestral: 6,
    anual: 12,
    bienal: 24,
  };
  const cycles = Math.max(1, Math.round(termMonths / monthsPerCycle[recurrence]));
  return cycles;
}

/** Fator para normalizar valor por ciclo em MRR (mensalidade). */
const MONTHLY_FACTOR: Record<RecurrenceInterval, number> = {
  semanal: 4.345,
  quinzenal: 2.1725,
  mensal: 1,
  bimestral: 0.5,
  trimestral: 0.3333,
  semestral: 0.1666,
  anual: 0.0833,
  bienal: 0.0416,
};

/** Toggle compacto % ↔ R$ (igual ao CreateChargeModal). */
function DiscountModeToggle({
  mode,
  onToggle,
}: {
  mode: DiscountMode;
  onToggle: (newMode: DiscountMode) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 overflow-hidden h-6">
      <button
        type="button"
        onClick={() => onToggle('percent')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'percent'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60',
        )}
        title="Desconto em percentual"
      >
        %
      </button>
      <button
        type="button"
        onClick={() => onToggle('fixed')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'fixed'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60',
        )}
        title="Desconto em valor fixo"
      >
        R$
      </button>
    </div>
  );
}

/**
 * Modal premium de criação de Recorrência (espelho do CreateChargeModal).
 */
export function CreateSubscriptionModal({ open, onOpenChange }: CreateSubscriptionModalProps) {

  // === Estados do formulário ===
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [selectedCustomerObj, setSelectedCustomerObj] = useState<PaginatedCustomer | null>(null);
  /** Veículos do cliente vinculados à assinatura (módulo Veículos) */
  const [vehicleIds, setVehicleIds] = useState<string[]>([]);

  // Limpa a seleção de veículos sempre que o cliente muda (os veículos
  // pertencem ao cliente selecionado).
  useEffect(() => {
    setVehicleIds([]);
  }, [customerId]);

  const [primeiroVencimento, setPrimeiroVencimento] = useState(
    format(addDays(new Date(), 3), 'yyyy-MM-dd'),
  );
  const [diasAntesEmissao, setDiasAntesEmissao] = useState<number>(12);
  // v1.2.0: modo de emissão da cobrança ('dias_antes' | 'dia_fixo')
  const [tipoEmissao, setTipoEmissao] = useState<'dias_antes' | 'dia_fixo'>('dias_antes');
  const [diaEmissao, setDiaEmissao] = useState<number>(1);
  const [meioPagamentoId, setMeioPagamentoId] = useState<string | null>(null);
  const [contaCorrenteId, setContaCorrenteId] = useState<string | null>(null);
  const [descricao, setDescricao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  // v1.3.0: nova seção colapsável de Juros e Multa por atraso
  const [juros, setJuros] = useState<JurosValue>(JUROS_DEFAULT);
  // v1.3.0: dias até a cobrança expirar após o vencimento (1..60)
  const [diasExpiracao, setDiasExpiracao] = useState<number>(60);
  const [itens, setItens] = useState<FormItem[]>([]);
  // Fase 5: cartão padrão (opcional)
  const [defaultCardTokenId, setDefaultCardTokenId] = useState<string | null>(null);
  const [discountModes, setDiscountModes] = useState<Record<string, DiscountMode>>({});
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);

  // Seletor de cliente
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  // Controla abertura do modal inline de criação de cliente (atalho "+")
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCustomerSearch(customerSearch), 300);
    return () => clearTimeout(t);
  }, [customerSearch]);

  // === Dados ===
  const { customers, isLoading: loadingCustomers } = useCrmCustomersPaginated(
    { search: debouncedCustomerSearch || null },
    { page: 1, pageSize: 20 },
  );
  // Catálogo já filtrado em apenas serviços recorrentes — usado para enriquecer o item ao adicionar
  const { products: recurringProducts } = useCrmProducts(
    { is_active: true, product_type: 'service', unit_type: 'recorrente' },
    { enabled: open },
  );
  const { meios, isLoading: loadingMeios } = useFinanceiroMeiosPagamento();
  const { contas, isLoading: loadingContas } = useFinanceiroContaCorrente();
  const { createSubscription, isCreating } = useCreateSubscription();

  // Filtros derivados
  const meiosAtivos = useMemo(() => meios.filter(m => m.is_active), [meios]);
  /**
   * v2026-09-12: inclui contas SEM integração de pagamento (controle manual).
   * Nesse caso as cobranças da recorrência ficam apenas locais ("Pendente"),
   * com baixa manual.
   */
  const contasComProvider = useMemo(
    () => contas.filter(c => c.is_active),
    [contas],
  );
  // Auto-selecionar conta única
  useEffect(() => {
    if (contasComProvider.length === 1 && !contaCorrenteId) {
      setContaCorrenteId(contasComProvider[0].id);
    }
  }, [contasComProvider, contaCorrenteId]);

  /**
   * v2026-09-12 (filtro por conta): meios exibidos são os vinculados à conta
   * selecionada + os GLOBAIS (sem conta vinculada). Evita misturar meios de
   * contas diferentes na mesma tela.
   */
  const meiosDaConta = useMemo(
    () =>
      meiosAtivos.filter(
        m => !m.conta_corrente_id || m.conta_corrente_id === contaCorrenteId
      ),
    [meiosAtivos, contaCorrenteId],
  );

  // Ao trocar a conta, desmarca o meio se ele não pertencer à nova conta
  // (e não for global), evitando combinação inválida.
  useEffect(() => {
    if (!meioPagamentoId) return;
    const meioAtual = meios.find(m => m.id === meioPagamentoId);
    if (
      meioAtual?.conta_corrente_id &&
      meioAtual.conta_corrente_id !== contaCorrenteId
    ) {
      setMeioPagamentoId(null);
    }
  }, [contaCorrenteId, meioPagamentoId, meios]);

  // === Limite Asaas para observações ===
  const MAX_OBS_ASAAS = 500;
  const contaSelecionada = contasComProvider.find(c => c.id === contaCorrenteId);
  const isProviderAsaas = contaSelecionada?.provider_slug === 'asaas';

  // Helpers de display do cliente
  const getCustomerDisplayName = (c: PaginatedCustomer) =>
    c.customer_type === 'pj'
      ? c.nome_fantasia || c.razao_social || 'Empresa sem nome'
      : c.nome || 'Cliente sem nome';
  const getCustomerDocument = (c: PaginatedCustomer) => c.cnpj || c.cpf || null;

  // === Frequência travada: derivada do primeiro item ===
  const frequenciaTravada: RecurrenceInterval | null = itens[0]?.recurrence_interval ?? null;
  const tipoPrazoTravado: TipoPrazoAssinatura | null =
    itens[0]?.term_type === 'determinado' ? 'determinado' :
    itens[0]?.term_type === 'indeterminado' ? 'indeterminado' :
    null;
  const ciclosTotalTravado: number | null = useMemo(() => {
    if (!itens[0] || !frequenciaTravada) return null;
    return calcularCiclosTotal(
      itens[0].term_type,
      itens[0].term_quantity,
      itens[0].term_unit,
      frequenciaTravada,
    );
  }, [itens, frequenciaTravada]);

  // === Cálculos por item (independente de mode: soma percent + fixed) ===
  // Como o toggle zera o outro lado, no máximo um valor é > 0 por item.
  const calcItemDiscount = useCallback((item: FormItem) => {
    const subtotal = item.quantidade * item.valor_unitario;
    const percentDisc = item.desconto_percent ? subtotal * (item.desconto_percent / 100) : 0;
    const fixedDisc = item.desconto_fixed || 0;
    return percentDisc + fixedDisc;
  }, []);

  // Subtotais
  const subtotalGeral = useMemo(
    () => itens.reduce((s, i) => s + i.quantidade * i.valor_unitario, 0),
    [itens],
  );
  const totalDescontos = useMemo(
    () => itens.reduce((s, i) => s + calcItemDiscount(i), 0),
    [itens, calcItemDiscount],
  );
  /** Valor cobrado a cada ciclo. */
  const valorPorCiclo = useMemo(
    () => subtotalGeral - totalDescontos,
    [subtotalGeral, totalDescontos],
  );
  /** MRR estimado (mensalidade equivalente). */
  const mrrEstimado = useMemo(() => {
    if (!frequenciaTravada || valorPorCiclo <= 0) return 0;
    return valorPorCiclo * (MONTHLY_FACTOR[frequenciaTravada] ?? 0);
  }, [valorPorCiclo, frequenciaTravada]);
  /** Total da vigência (somente quando prazo determinado). */
  const totalVigencia = useMemo(() => {
    if (tipoPrazoTravado !== 'determinado' || !ciclosTotalTravado) return null;
    return valorPorCiclo * ciclosTotalTravado;
  }, [valorPorCiclo, ciclosTotalTravado, tipoPrazoTravado]);

  // === Handlers de itens ===

  /** Adicionar item — aplica trava de frequência única. */
  const handleCatalogAddItem = useCallback(
    async (productId: string, quantity: number, unitPrice: number) => {
      const prod: CrmProduct | undefined = recurringProducts.find(p => p.id === productId);
      if (!prod) {
        toast.error('Produto não encontrado no catálogo.');
        return;
      }
      if (!prod.recurrence_interval) {
        toast.error('Serviço inválido: sem periodicidade configurada.');
        return;
      }

      // Trava: precisa coincidir com a frequência já travada
      if (frequenciaTravada && prod.recurrence_interval !== frequenciaTravada) {
        toast.error(
          `Não é possível misturar serviços com frequências diferentes na mesma recorrência. ` +
          `Atual: ${formatRecurrenceLabel(frequenciaTravada)}.`,
          { duration: 6000 },
        );
        return;
      }

      // Se já existe item desse produto, soma quantidade
      const existing = itens.find(i => i.product_id === productId);
      if (existing) {
        setItens(prev => prev.map(i =>
          i.product_id === productId ? { ...i, quantidade: i.quantidade + quantity } : i,
        ));
        return;
      }

      const novo: FormItem = {
        id: crypto.randomUUID(),
        product_id: productId,
        product_name: prod.name,
        product_sku: prod.sku || null,
        descricao: prod.name,
        quantidade: quantity,
        // unitPrice já vem de ProductListRow como recurring_price quando onlyRecurring=true.
        valor_unitario: unitPrice || prod.recurring_price || 0,
        desconto_percent: 0,
        desconto_fixed: 0,
        recurrence_interval: prod.recurrence_interval,
        term_type: prod.term_type ?? null,
        term_quantity: prod.term_quantity ?? null,
        term_unit: prod.term_unit ?? null,
      };
      setItens(prev => [...prev, novo]);
    },
    [recurringProducts, frequenciaTravada, itens],
  );

  const handleCatalogUpdateQuantity = useCallback(async (productId: string, quantity: number) => {
    setItens(prev => prev.map(i => i.product_id === productId ? { ...i, quantidade: quantity } : i));
  }, []);
  const handleCatalogRemoveItem = useCallback(async (productId: string) => {
    setItens(prev => prev.filter(i => i.product_id !== productId));
  }, []);
  const handleRemoveItem = useCallback((itemId: string) => {
    setItens(prev => prev.filter(i => i.id !== itemId));
  }, []);
  const handleUpdateItem = useCallback(
    (itemId: string, field: keyof FormItem, value: number | string) => {
      setItens(prev => prev.map(i => i.id === itemId ? { ...i, [field]: value } : i));
    }, []);

  // Itens no formato esperado pelo ProductCatalogModal
  const itensForCatalog = useMemo(
    () => itens.map(i => ({ product_id: i.product_id, quantity: i.quantidade })),
    [itens],
  );

  /** Formatar moeda BRL. */
  const formatBRL = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  // v1.4.0 — Padrões do tenant (UI prefill apenas) para juros/multa e expiração.
  const chargeDefaults = useChargeDefaults();

  /** Reset completo do formulário. */
  const resetForm = useCallback(() => {
    setCustomerId(null);
    setSelectedCustomerObj(null);
    setCustomerSearch('');
    setPrimeiroVencimento(format(addDays(new Date(), 3), 'yyyy-MM-dd'));
    setDiasAntesEmissao(12);
    setTipoEmissao('dias_antes');
    setDiaEmissao(1);
    setMeioPagamentoId(null);
    setContaCorrenteId(null);
    setDescricao('');
    setObservacoes('');
    // v1.4.0 — aplica padrões do tenant se ligados
    setJuros(chargeDefaults.jurosEnabled ? chargeDefaults.juros : JUROS_DEFAULT);
    setDiasExpiracao(chargeDefaults.expiracaoEnabled ? chargeDefaults.diasExpiracao : 60);
    setItens([]);
    setDiscountModes({});
    setDefaultCardTokenId(null);

  }, [chargeDefaults]);

  /**
   * v1.4.0 — Aplica padrões do tenant na primeira abertura do modal.
   * Roda novamente quando os defaults terminam de carregar após abertura.
   */
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (!open) { setDefaultsApplied(false); return; }
    if (chargeDefaults.isLoading) return;
    if (defaultsApplied) return;
    if (chargeDefaults.jurosEnabled) setJuros(chargeDefaults.juros);
    if (chargeDefaults.expiracaoEnabled) setDiasExpiracao(chargeDefaults.diasExpiracao);
    setDefaultsApplied(true);
  }, [open, chargeDefaults, defaultsApplied]);


  // === Validação ===
  const isValid = useMemo(() => {
    if (!customerId) return false;
    if (selectedCustomerObj && !selectedCustomerObj.cpf && !selectedCustomerObj.cnpj) return false;
    if (itens.length === 0) return false;
    if (valorPorCiclo < VALOR_MINIMO_COBRANCA) return false;
    if (!meioPagamentoId) return false;
    if (!contaCorrenteId) return false;
    if (!primeiroVencimento) return false;
    if (!frequenciaTravada) return false;
    if (tipoPrazoTravado === 'determinado' && (!ciclosTotalTravado || ciclosTotalTravado < 1)) return false;
    return true;
  }, [
    customerId, selectedCustomerObj, itens, valorPorCiclo,
    meioPagamentoId, contaCorrenteId, primeiroVencimento,
    frequenciaTravada, tipoPrazoTravado, ciclosTotalTravado,
  ]);

  /** Submeter assinatura. */
  const handleSubmit = async () => {
    if (!isValid || !customerId || !meioPagamentoId || !contaCorrenteId || !frequenciaTravada) return;

    // Monta itens persistindo o TIPO de desconto escolhido pelo usuário
    // (percent | fixed) + o percentual original. Esses campos são gravados em
    // financeiro_cobranca_itens.metadata pela RPC create_financeiro_assinatura
    // (v1.0.1) e devolvidos por get_assinatura_details (v1.2.1), permitindo que
    // o modal de edição reabra o toggle %/R$ fielmente como salvo.
    const subscriptionItens = itens.map(i => {
      const mode: DiscountMode = discountModes[i.id] ?? 'fixed';
      return {
        product_id: i.product_id,
        descricao: i.descricao,
        quantidade: i.quantidade,
        valor_unitario: i.valor_unitario,
        desconto: Number(calcItemDiscount(i).toFixed(2)),
        desconto_tipo: mode,
        desconto_percent: mode === 'percent' ? Number((i.desconto_percent || 0).toFixed(4)) : 0,
      };
    });

    try {
      await createSubscription.mutateAsync({
        customer_id: customerId,
        meio_pagamento_id: meioPagamentoId,
        conta_corrente_id: contaCorrenteId,
        itens: subscriptionItens,
        primeiro_vencimento: primeiroVencimento,
        frequencia: frequenciaTravada as FrequenciaAssinatura,
        tipo_prazo: tipoPrazoTravado || 'indeterminado',
        ciclos_total: tipoPrazoTravado === 'determinado' ? ciclosTotalTravado : null,
        dias_antes_emissao: diasAntesEmissao,
        tipo_emissao: tipoEmissao,
        dia_emissao: tipoEmissao === 'dia_fixo' ? diaEmissao : null,
        descricao: descricao.trim() || itens.map(i => i.descricao).join(', '),
        observacoes: observacoes || undefined,
        juros: juros.enabled ? (juros as unknown as Record<string, unknown>) : {},
        dias_expiracao: diasExpiracao,
        default_card_token_id: defaultCardTokenId,
        // Veículos vinculados (gravados na mesma transação da assinatura)
        vehicle_ids: vehicleIds.length > 0 ? vehicleIds : undefined,
      });
      resetForm();
      onOpenChange(false);
    } catch (err) {
      logger.error('Erro ao criar assinatura', { error: String(err) });
    }
  };


  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <Repeat className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg">Nova Recorrência</DialogTitle>
              <DialogDescription className="text-sm">
                Configure uma assinatura recorrente reaproveitando os serviços do catálogo.
              </DialogDescription>
            </div>
          </div>

        </DialogHeader>


        {/* Corpo com scroll */}
        <ScrollArea className="flex-1 overflow-auto">
          <div className="p-6 space-y-6">

            {/* === SEÇÃO 1: Cliente === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <User className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Cliente</h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              <div className="flex items-center gap-2">
                <Popover open={customerPopoverOpen} onOpenChange={setCustomerPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={customerPopoverOpen}
                      className="flex-1 justify-between"
                    >
                      {selectedCustomerObj ? (
                        <div className="flex items-center gap-2 truncate">
                          <Badge variant="outline" className="text-[10px] px-1.5 shrink-0">
                            {selectedCustomerObj.customer_type === 'pj' ? 'PJ' : 'PF'}
                          </Badge>
                          <span className="truncate">{getCustomerDisplayName(selectedCustomerObj)}</span>
                          {getCustomerDocument(selectedCustomerObj) && (
                            <span className="text-xs text-muted-foreground ml-1 shrink-0">
                              {formatDocumentForDisplay(getCustomerDocument(selectedCustomerObj))}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Selecione um cliente...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Buscar por nome, CPF/CNPJ, telefone, email..."
                        value={customerSearch}
                        onValueChange={setCustomerSearch}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {loadingCustomers ? 'Buscando...' : 'Nenhum cliente encontrado.'}
                        </CommandEmpty>
                        <CommandGroup>
                          {customers.map((customer) => (
                            <CommandItem
                              key={customer.id}
                              value={customer.id}
                              onSelect={() => {
                                setCustomerId(customer.id);
                                setSelectedCustomerObj(customer);
                                setCustomerPopoverOpen(false);
                                setCustomerSearch('');
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Badge variant="outline" className="text-[10px] px-1.5 shrink-0">
                                  {customer.customer_type === 'pj' ? 'PJ' : 'PF'}
                                </Badge>
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="truncate font-medium">
                                    {getCustomerDisplayName(customer)}
                                  </span>
                                  {(getCustomerDocument(customer) || customer.main_contact_phone || customer.main_contact_email) && (
                                    <span className="text-xs text-muted-foreground truncate">
                                      {[
                                        formatDocumentForDisplay(getCustomerDocument(customer)),
                                        customer.main_contact_phone,
                                        customer.main_contact_email,
                                      ].filter(Boolean).join(' • ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Check
                                className={cn(
                                  'ml-2 h-4 w-4 shrink-0',
                                  customerId === customer.id ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Atalho "+" — abre modal inline para criar novo cliente sem sair da recorrência */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Criar novo cliente"
                  className="shrink-0"
                  onClick={() => setCreateCustomerOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Modal inline de criação de cliente — pré-seleciona o cliente recém-criado */}
              <CustomerFormDialog
                customer={null}
                open={createCustomerOpen}
                onOpenChange={setCreateCustomerOpen}
                onSuccess={(newCustomer) => {
                  if (newCustomer) {
                    setCustomerId(newCustomer.id);
                    setSelectedCustomerObj({
                      id: newCustomer.id,
                      nome: newCustomer.customer_type === 'pf' ? newCustomer.nome : null,
                      razao_social: newCustomer.customer_type === 'pj' ? newCustomer.nome : null,
                      nome_fantasia: newCustomer.customer_type === 'pj' ? newCustomer.nome : null,
                      customer_type: newCustomer.customer_type as 'pf' | 'pj',
                      cpf: newCustomer.cpf || null,
                      cnpj: newCustomer.cnpj || null,
                      contact_phone: newCustomer.contact_phone || null,
                      contact_email: newCustomer.contact_email || null,
                      main_contact_id: newCustomer.main_contact_id,
                    } as unknown as PaginatedCustomer);
                  }
                  setCreateCustomerOpen(false);
                }}
              />

              {/* Alerta CPF/CNPJ ausente — bloqueia criação */}
              {customerId && selectedCustomerObj && !selectedCustomerObj.cpf && !selectedCustomerObj.cnpj && (
                <Alert variant="destructive" className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Cliente sem CPF/CNPJ cadastrado. Complete o cadastro antes de gerar a recorrência.
                  </AlertDescription>
                </Alert>
              )}
            </section>

            <Separator />

            {/* === SEÇÃO 1.5: Descrição (opcional, fallback automático para itens) === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Descrição</h3>
                <span className="text-[10px] text-muted-foreground">(opcional)</span>
              </div>
              {/* Campo com variáveis dinâmicas: o template é salvo na assinatura
                  e cada ciclo emitido renderiza a descrição atualizada. */}
              <DescricaoTemplateField
                value={descricao}
                onChange={setDescricao}
                placeholder="Deixe em branco para gerar automaticamente a partir dos itens"
                maxLength={200}
                customerId={customerId || null}
                vehicleIds={vehicleIds}
              />

              <p className="text-xs text-muted-foreground mt-1">
                Se vazio, será preenchido com a lista dos itens da recorrência.
              </p>
            </section>

            <Separator />

            {/* === SEÇÃO 2: Serviços (Catálogo recorrente) === */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Serviços Recorrentes</h3>
                  <span className="text-xs text-destructive">*</span>
                  {itens.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                    </Badge>
                  )}
                </div>
                {itens.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setCatalogModalOpen(true)} className="h-7 text-xs">
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Adicionar
                  </Button>
                )}
              </div>

              {/* Reusa DealItemsSummary com variante 'subscription':
                  sem cabeçalho duplicado, sem agrupamento e sem colunas
                  Unidade/Preço (que não fazem sentido em recorrência) */}
              <DealItemsSummary
                variant="subscription"
                items={itens.map((i) => ({
                  id: i.id,
                  product_id: i.product_id,
                  quantity: i.quantidade,
                  unit_price: i.valor_unitario,
                  discount_percent: i.desconto_percent || null,
                  discount_fixed: i.desconto_fixed || null,
                  // Garante que o toggle reflita o modo escolhido pelo usuário,
                  // mesmo quando o valor numérico do outro lado for zero.
                  discount_mode: discountModes[i.id] ?? null,
                  total: i.quantidade * i.valor_unitario - calcItemDiscount(i),
                  product: {
                    id: i.product_id,
                    name: i.product_name,
                    sku: i.product_sku,
                    product_type: 'service',
                    unit_type: 'recorrente',
                    recurring_price: i.valor_unitario,
                    recurrence_interval: i.recurrence_interval,
                    term_type: i.term_type,
                    term_quantity: i.term_quantity,
                    term_unit: i.term_unit,
                  },
                }))}
                currency="BRL"
                readOnly={false}
                onUpdateQuantity={(itemId, qty) =>
                  handleUpdateItem(itemId, 'quantidade', Math.max(1, qty))
                }
                onUpdateDiscount={(itemId, percent, fixed) => {
                  handleUpdateItem(itemId, 'desconto_percent', percent || 0);
                  handleUpdateItem(itemId, 'desconto_fixed', fixed || 0);
                  // Sincroniza o modo persistido em metadata (desconto_tipo)
                  // com o lado realmente preenchido pelo usuário.
                  setDiscountModes((prev) => ({
                    ...prev,
                    [itemId]: (percent || 0) > 0 ? 'percent' : 'fixed',
                  }));
                }}
                onRemove={(itemId) => handleRemoveItem(itemId)}
                onOpenCatalog={() => setCatalogModalOpen(true)}
              />

              {/* Alerta valor mínimo Asaas */}
              {valorPorCiclo > 0 && valorPorCiclo < VALOR_MINIMO_COBRANCA && (
                <Alert variant="destructive" className="mt-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    O valor mínimo por ciclo é de <strong>R$ 5,00</strong>. Ajuste quantidade,
                    valor ou desconto.
                  </AlertDescription>
                </Alert>
              )}

            </section>

            {/* Catálogo de produtos — filtrado APENAS para serviços recorrentes */}
            <ProductCatalogModal
              open={catalogModalOpen}
              onOpenChange={setCatalogModalOpen}
              dealId=""
              currency="BRL"
              dealItems={itensForCatalog}
              onAddItem={handleCatalogAddItem}
              onUpdateItemQuantity={handleCatalogUpdateQuantity}
              onRemoveItem={handleCatalogRemoveItem}
              onlyRecurring
            />

            <Separator />

            {/* === SEÇÃO 3: Primeiro Vencimento + Emissão da cobrança === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Vencimento e Emissão</h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    Primeiro Vencimento
                  </Label>
                  <Input
                    type="date"
                    value={primeiroVencimento}
                    onChange={(e) => setPrimeiroVencimento(e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    Emissão da cobrança
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          Define quando o sistema vai gerar (status "pendente") cada fatura recorrente:
                          N dias antes do vencimento OU sempre num dia fixo do mês. Em meses sem o dia
                          escolhido (ex.: 31/02), usa o último dia do mês.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <RadioGroup
                    value={tipoEmissao}
                    onValueChange={(v) => setTipoEmissao(v as 'dias_antes' | 'dia_fixo')}
                    className="flex flex-wrap items-center gap-4 mb-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="dias_antes" id="emis-dias-antes-create" />
                      <Label htmlFor="emis-dias-antes-create" className="text-xs cursor-pointer">
                        Dias antes do vencimento
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="dia_fixo" id="emis-dia-fixo-create" />
                      <Label htmlFor="emis-dia-fixo-create" className="text-xs cursor-pointer">
                        Dia específico do mês
                      </Label>
                    </div>
                  </RadioGroup>
                  {tipoEmissao === 'dias_antes' ? (
                    <Input
                      type="number"
                      min={1}
                      max={60}
                      value={diasAntesEmissao}
                      onChange={(e) =>
                        setDiasAntesEmissao(Math.max(1, Math.min(60, Number(e.target.value) || 12)))
                      }
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Dia</span>
                      <Input
                        type="number"
                        min={1}
                        max={31}
                        value={diaEmissao}
                        onChange={(e) =>
                          setDiaEmissao(Math.max(1, Math.min(31, Number(e.target.value) || 1)))
                        }
                        className="w-20"
                      />
                      <span className="text-xs text-muted-foreground">do mês</span>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {tipoEmissao === 'dias_antes'
                  ? `O 1º ciclo é emitido imediatamente. Os demais são emitidos automaticamente ${diasAntesEmissao} dias antes de cada vencimento.`
                  : `O 1º ciclo é emitido imediatamente. Os demais são emitidos sempre no dia ${diaEmissao} de cada mês (clamp ao último dia em meses curtos).`}
              </p>
            </section>


            <Separator />

            {/* === SEÇÃO 4: Conta Corrente (antes dos meios — filtra a lista) === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Conta Corrente</h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              {loadingContas ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
                </div>
              ) : contasComProvider.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Nenhuma conta corrente ativa cadastrada.
                </div>
              ) : (
                <Select value={contaCorrenteId || ''} onValueChange={(v) => setContaCorrenteId(v || null)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione a conta corrente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contasComProvider.map(conta => (
                      <SelectItem key={conta.id} value={conta.id}>
                        <div className="flex items-center gap-2">
                          <span>{conta.nome}</span>
                          {/* Sem provider => controle manual */}
                          <Badge variant="outline" className="text-[10px]">
                            {conta.provider_slug || 'Manual'}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {/* Aviso de conta sem integração (recorrência de controle interno) */}
              {contaSelecionada && !contaSelecionada.provider_slug && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Conta sem integração de pagamento — as cobranças desta recorrência
                  serão apenas de controle interno, com baixa manual.
                </p>
              )}
            </section>

            <Separator />

            {/* === SEÇÃO 5: Meio de Pagamento (filtrado pela conta selecionada) === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Meio de Pagamento</h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              {loadingMeios ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
                </div>
              ) : !contaCorrenteId ? (
                /* Conta ainda não selecionada: orienta o usuário antes de listar meios */
                <InlineNotice variant="info">
                  Selecione a conta corrente para ver os meios de pagamento disponíveis.
                </InlineNotice>
              ) : meiosDaConta.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Nenhum meio de pagamento ativo para esta conta. Configure em Configurações &gt; Meios de Pagamento.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {meiosDaConta.map(meio => (
                    <button
                      key={meio.id}
                      type="button"
                      onClick={() => setMeioPagamentoId(meio.id)}
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        meioPagamentoId === meio.id
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/40',
                      )}
                    >
                      {(() => {
                        const IconComp = PaymentMethodIcon[meio.tipo_pagamento] || DefaultPaymentIcon;
                        return <div className="mb-1"><IconComp className="h-6 w-6 text-muted-foreground" /></div>;
                      })()}
                      <div className="font-medium text-xs truncate">{meio.nome}</div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <Separator />

            {/* === SEÇÃO 6: Observações === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Observações</h3>
                <span className="text-[10px] text-muted-foreground">(opcional)</span>
              </div>
              <Textarea
                placeholder="Notas internas sobre esta recorrência..."
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={3}
                className="resize-none"
                maxLength={isProviderAsaas ? MAX_OBS_ASAAS : undefined}
              />
              {isProviderAsaas && (
                <p className="text-xs text-muted-foreground text-right mt-1">
                  {observacoes.length}/{MAX_OBS_ASAAS} caracteres
                </p>
              )}
            </section>

            {/* === Veículos vinculados à assinatura (módulo Veículos) === */}
            <VehiclePickerSection
              customerId={customerId}
              contactId={selectedCustomerObj?.main_contact_id ?? null}
              value={vehicleIds}
              onChange={setVehicleIds}
              enabled={open}
              className="pt-1"
            />

            <Separator />

            {/* === SEÇÃO 7: Juros e Multa por atraso (colapsável) === */}
            <SubscriptionJurosSection value={juros} onChange={setJuros} />

            {/* === SEÇÃO 8: Expiração da cobrança (colapsável, 1..60 dias) === */}
            {/* v2.3.0 — oculto para conta sem integração (controle manual) */}
            {!(contaSelecionada && !contaSelecionada.provider_slug) && (
              <SubscriptionExpiracaoSection value={diasExpiracao} onChange={setDiasExpiracao} />
            )}

            {/* === SEÇÃO 9 (Fase 5): Cartão padrão da assinatura === */}
            {customerId && (
              <>
                <Separator />
                <section className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">Cartão padrão (opcional)</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Se o cliente já tem cartões salvos, selecione um para ser usado automaticamente
                    nos próximos ciclos. Caso contrário, um link de checkout será enviado em cada
                    cobrança.
                  </p>
                  <SavedCardsList
                    customerId={customerId}
                    selectable
                    canRevoke={false}
                    selectedCardId={defaultCardTokenId}
                    onSelect={setDefaultCardTokenId}
                  />
                </section>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t shrink-0 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {valorPorCiclo > 0 && valorPorCiclo < VALOR_MINIMO_COBRANCA ? (
              <span className="text-destructive font-medium">⚠ Valor abaixo do mínimo (R$ 5,00)</span>
            ) : valorPorCiclo > 0 && frequenciaTravada ? (
              <span>
                {formatBRL(valorPorCiclo)} / {formatRecurrenceLabel(frequenciaTravada).toLowerCase()}
                {totalVigencia != null && (
                  <> • Total: <span className="font-bold text-foreground">{formatBRL(totalVigencia)}</span></>
                )}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { resetForm(); onOpenChange(false); }}
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isCreating}
              className="min-w-[160px]"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Repeat className="h-4 w-4 mr-2" />
                  Criar Recorrência
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
